/**
 * CORD-05 §6 Direct Invites: the invite bundle giftwrapped straight to an
 * npub as a kind 3313 rumor. This is how the moderator's approval reaches an
 * applicant — in Armada the very same wrap surfaces as a community invite.
 */

import type { NostrEvent } from "@nostrify/nostrify";

import { KIND_DIRECT_INVITE } from "./config";
import { communityId } from "./crypto";
import { createGiftWrap, unwrapGiftWrap, type GiftSigner } from "./stream";
import type { InviteBundle, Membership } from "./types";

/** Build the invite bundle from the moderator's own membership material. */
export function bundleFromMembership(m: Membership, label?: string): InviteBundle {
  return {
    community_id: m.communityId,
    owner: m.current.owner,
    owner_salt: m.current.owner_salt,
    community_root: m.current.community_root,
    root_epoch: m.current.root_epoch,
    control_pk: m.current.control_pk,
    // Public channels derive from the community_root; only private channel
    // keys travel in the bundle (CORD-02 §7).
    channels: m.current.channels.filter((c) => c.key),
    relays: m.current.relays,
    name: m.current.name,
    creator_npub: m.current.owner,
    ...(label ? { label } : {}),
  };
}

/**
 * Validate a received bundle (CORD-05 §1): the community_id must self-certify
 * the owner, and attacker-controlled bounds are enforced before allocating.
 */
export async function validateBundle(b: unknown): Promise<InviteBundle | null> {
  if (!b || typeof b !== "object") return null;
  const r = b as Record<string, unknown>;
  const hex64 = (v: unknown): v is string =>
    typeof v === "string" && /^[0-9a-f]{64}$/.test(v);
  if (!hex64(r.community_id) || !hex64(r.owner) || !hex64(r.owner_salt)) return null;
  if (!hex64(r.community_root)) return null;
  const epoch = Number(r.root_epoch ?? 0);
  if (!Number.isInteger(epoch) || epoch < 0) return null;
  const channels = Array.isArray(r.channels) ? r.channels.slice(0, 256) : [];
  const relays = Array.isArray(r.relays)
    ? (r.relays.filter((x) => typeof x === "string") as string[]).slice(0, 5)
    : [];
  if (relays.length === 0) return null;
  if (
    r.expires_at !== undefined &&
    (typeof r.expires_at !== "number" || r.expires_at < Date.now())
  ) {
    return null; // expired invite refuses the join
  }

  const expected = await communityId(r.owner as string, hex64toBytes(r.owner_salt as string));
  if (expected !== r.community_id) return null;

  return {
    community_id: r.community_id,
    owner: r.owner,
    owner_salt: r.owner_salt,
    community_root: r.community_root,
    root_epoch: epoch,
    control_pk: hex64(r.control_pk) ? r.control_pk : undefined,
    channels: channels
      .map((c) => {
        if (!c || typeof c !== "object") return null;
        const cr = c as Record<string, unknown>;
        const id = hex64(cr.id) ? cr.id : undefined;
        if (!id) return null;
        return {
          id,
          key: hex64(cr.key) ? cr.key : undefined,
          epoch: Number.isInteger(Number(cr.epoch)) ? Number(cr.epoch) : undefined,
          name: typeof cr.name === "string" ? cr.name : undefined,
        };
      })
      .filter((c): c is NonNullable<typeof c> => c !== null),
    relays,
    name: typeof r.name === "string" ? r.name : "",
    expires_at: typeof r.expires_at === "number" ? r.expires_at : undefined,
    creator_npub: hex64(r.creator_npub) ? r.creator_npub : undefined,
    label: typeof r.label === "string" ? r.label : undefined,
  };
}

function hex64toBytes(hex: string): Uint8Array {
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Giftwrap a Direct Invite (kind 3313) to a recipient, `k`-tagged for indexing. */
export async function createDirectInvite(opts: {
  signer: GiftSigner;
  inviterPub: string;
  recipientPub: string;
  bundle: InviteBundle;
}): Promise<NostrEvent> {
  return createGiftWrap({
    signer: opts.signer,
    senderPub: opts.inviterPub,
    recipientPub: opts.recipientPub,
    rumorKind: KIND_DIRECT_INVITE,
    content: JSON.stringify(opts.bundle),
    outerTags: [["k", String(KIND_DIRECT_INVITE)]],
  });
}

export interface ReceivedInvite {
  inviter: string;
  bundle: InviteBundle;
  wrapId: string;
}

/** Unwrap a kind-3313-tagged giftwrap and validate the bundle inside. */
export async function unwrapDirectInvite(
  wrap: NostrEvent,
  signer: GiftSigner,
): Promise<ReceivedInvite | null> {
  const opened = await unwrapGiftWrap(wrap, signer);
  if (!opened) return null;
  if (opened.rumor.kind !== KIND_DIRECT_INVITE) return null;
  try {
    const bundle = await validateBundle(JSON.parse(opened.rumor.content));
    if (!bundle) return null;
    return { inviter: opened.rumor.pubkey, bundle, wrapId: wrap.id };
  } catch {
    return null;
  }
}
